import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Tabs, Input, Select, Button, Progress, Space, Modal, Checkbox, App, DatePicker, Segmented, Tooltip, Avatar } from 'antd';
import { Plus, Download, Settings, Search, LayoutList, LayoutGrid, Calendar as CalIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { taskService } from '@/services/taskService';
import { PageHeader } from '@/components/common/PageHeader';
import { TaskStatusBadge, PriorityBadge } from '@/components/common/Badges';
import { useAuthStore } from '@/store/authStore';
import type { Task } from '@/types';

type Tab = 'assigned-to-me' | 'assigned-by-me' | 'watching' | 'collaborating' | 'my-department' | 'archived' | 'all';

type ColumnKey = 'code' | 'title' | 'status' | 'priority' | 'assigner' | 'assignee'
  | 'department' | 'category' | 'dueDate' | 'startDate' | 'progress'
  | 'createdAt' | 'completedAt' | 'estimatedHours' | 'tags';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultVisible: boolean;
  width?: number;
  render?: (v: any, task: Task) => React.ReactNode;
  excelValue?: (task: Task) => string | number | Date | undefined;
}

const STATUS_LABELS: Record<string, string> = {
  NEW: 'Mới', ASSIGNED: 'Đã giao', IN_PROGRESS: 'Đang làm',
  PENDING_REVIEW: 'Chờ duyệt', COMPLETED: 'Hoàn thành',
  CANCELLED: 'Đã hủy', OVERDUE: 'Quá hạn',
};
const PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Thấp', MEDIUM: 'Trung bình', HIGH: 'Cao', URGENT: 'Khẩn cấp', CRITICAL: 'Tới hạn',
};

const ALL_COLUMNS: ColumnDef[] = [
  {
    key: 'code', label: 'Mã task', defaultVisible: true, width: 100,
    render: (v: string) => <span className="mono" style={{ fontSize: 11, color: '#7d8a98' }}>{v}</span>,
    excelValue: (t) => t.code,
  },
  {
    key: 'title', label: 'Tiêu đề', defaultVisible: true,
    render: (_, task: Task) => (
      <div>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{task.title}</div>
        <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 2 }}>
          {task.subtaskCount ? `${task.subtaskCount} task con · ` : ''}
          {task.commentCount ?? 0} bình luận · {task.attachmentCount ?? 0} tệp
        </div>
      </div>
    ),
    excelValue: (t) => t.title,
  },
  {
    key: 'status', label: 'Trạng thái', defaultVisible: true, width: 120,
    render: (s: Task['status']) => <TaskStatusBadge status={s} />,
    excelValue: (t) => STATUS_LABELS[t.status] ?? t.status,
  },
  {
    key: 'priority', label: 'Ưu tiên', defaultVisible: true, width: 90,
    render: (p: Task['priority']) => <PriorityBadge priority={p} />,
    excelValue: (t) => PRIORITY_LABELS[t.priority] ?? t.priority,
  },
  {
    key: 'assigner', label: 'Người giao', defaultVisible: true, width: 130,
    render: (a: Task['assigner']) => a?.fullName ?? '-',
    excelValue: (t) => t.assigner?.fullName ?? '',
  },
  {
    key: 'assignee', label: 'Người thực hiện', defaultVisible: false, width: 160,
    render: (_, task: Task) => (task.assignees ?? []).map(a => a.user?.fullName).filter(Boolean).join(', ') || '-',
    excelValue: (t) => (t.assignees ?? []).map(a => a.user?.fullName).filter(Boolean).join(', '),
  },
  {
    key: 'department', label: 'Khoa/Phòng', defaultVisible: false, width: 140,
    render: (_, task: Task) => task.department?.name ?? '-',
    excelValue: (t) => t.department?.name ?? '',
  },
  {
    key: 'category', label: 'Loại task', defaultVisible: false, width: 110,
    render: (c: string) => c ?? '-',
    excelValue: (t) => (t as any).category ?? '',
  },
  {
    key: 'dueDate', label: 'Hạn', defaultVisible: true, width: 110,
    render: (d: string, task: Task) => {
      const overdue = task.status === 'OVERDUE'
        || (d && dayjs(d).isBefore(dayjs(), 'day') && task.status !== 'COMPLETED');
      if (!d) return '-';
      // V1.0.63h: Task quá hạn - badge đỏ TO + đậm để dễ nhận biết
      return overdue ? (
        <span style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#ffffff',
          background: '#dc2626',
          padding: '3px 8px',
          borderRadius: 6,
          display: 'inline-block',
          boxShadow: '0 1px 3px rgba(220, 38, 38, 0.4)',
          fontFamily: 'ui-monospace, monospace',
        }}>
          ⚠ {dayjs(d).format('DD/MM')}
        </span>
      ) : (
        <span className="mono" style={{ fontSize: 11, color: '#48535e' }}>
          {dayjs(d).format('DD/MM')}
        </span>
      );
    },
    excelValue: (t) => t.dueDate ? dayjs(t.dueDate).format('DD/MM/YYYY') : '',
  },
  {
    key: 'startDate', label: 'Ngày bắt đầu', defaultVisible: false, width: 110,
    render: (d: string) => d ? <span className="mono" style={{ fontSize: 11 }}>{dayjs(d).format('DD/MM/YYYY')}</span> : '-',
    excelValue: (t) => t.startDate ? dayjs(t.startDate).format('DD/MM/YYYY') : '',
  },
  {
    key: 'progress', label: 'Tiến độ', defaultVisible: true, width: 120,
    render: (p: number) => (
      <div>
        <Progress percent={p ?? 0} showInfo={false} size="small" strokeColor="#6699CC" />
        <div className="mono" style={{ fontSize: 10, color: '#7d8a98', marginTop: 2 }}>{p ?? 0}%</div>
      </div>
    ),
    excelValue: (t) => `${t.progress ?? 0}%`,
  },
  {
    key: 'createdAt', label: 'Ngày tạo', defaultVisible: false, width: 110,
    render: (d: string) => d ? <span className="mono" style={{ fontSize: 11 }}>{dayjs(d).format('DD/MM/YYYY')}</span> : '-',
    excelValue: (t) => (t as any).createdAt ? dayjs((t as any).createdAt).format('DD/MM/YYYY HH:mm') : '',
  },
  {
    key: 'completedAt', label: 'Ngày hoàn thành', defaultVisible: false, width: 130,
    render: (d: string) => d ? <span className="mono" style={{ fontSize: 11 }}>{dayjs(d).format('DD/MM/YYYY')}</span> : '-',
    excelValue: (t) => (t as any).completedAt ? dayjs((t as any).completedAt).format('DD/MM/YYYY HH:mm') : '',
  },
  {
    key: 'estimatedHours', label: 'Giờ ước tính', defaultVisible: false, width: 100,
    render: (h: number) => h ?? '-',
    excelValue: (t) => (t as any).estimatedHours ?? '',
  },
  {
    key: 'tags', label: 'Tags', defaultVisible: false, width: 150,
    render: (_, task: Task) => ((task as any).tags ?? []).join(', ') || '-',
    excelValue: (t) => ((t as any).tags ?? []).join(', '),
  },
];

const LOCAL_STORAGE_KEY = 'hv-taskflow-task-columns-v1';

function loadVisibleColumns(): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as ColumnKey[];
      if (Array.isArray(arr) && arr.length > 0) return new Set(arr);
    }
  } catch { /* ignore */ }
  return new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
}

function saveVisibleColumns(keys: Set<ColumnKey>) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.from(keys)));
  } catch { /* ignore */ }
}

export function TaskListPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const currentUser = useAuthStore(s => s.user);
  const hasPermission = useAuthStore(s => s.hasPermission);
  const hasRole = useAuthStore(s => s.hasRole);
  // V1.0.34: user không có quyền task:view_all sẽ chỉ thấy task của mình trong tab "Tất cả"
  const canViewAll = hasPermission('task:view_all');
  // V1.0.49: Tab "Task khoa tôi" chỉ hiện cho DEPT_HEAD (trưởng khoa) hoặc cao hơn
  const isDeptHead = hasRole('DEPT_HEAD') || hasRole('BOD') || hasRole('SYS_ADMIN');
  const [tab, setTab] = useState<Tab>('assigned-to-me');
  const [search, setSearch] = useState('');

  // V1.0.35: Đồng bộ search từ URL param (Global Search trên header navigate đến đây)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch && urlSearch !== search) {
      setSearch(urlSearch);
      setTab('all'); // search global → mở tab "Tất cả" để tìm rộng nhất
      // Clear URL param sau khi đã set state để tránh tự động set lại khi user xóa input
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('search');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams]);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  // V1.0.26: bỏ priorityFilter cũ — thay bằng urgentLevel + importantLevel
  const [urgentLevelFilter, setUrgentLevelFilter] = useState<number | undefined>();
  const [importantLevelFilter, setImportantLevelFilter] = useState<number | undefined>();
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(loadVisibleColumns);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // New filters: tags + due date range
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [dueFrom, setDueFrom] = useState<dayjs.Dayjs | null>(null);
  const [dueTo, setDueTo] = useState<dayjs.Dayjs | null>(null);

  // View mode: list (Table) hoặc kanban (drag-drop board) — persist localStorage
  const TASK_VIEW_KEY = 'hv-task-view-mode';
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(() => {
    const saved = localStorage.getItem(TASK_VIEW_KEY);
    return (saved === 'kanban' || saved === 'list') ? saved : 'list';
  });
  useEffect(() => { localStorage.setItem(TASK_VIEW_KEY, viewMode); }, [viewMode]);

  // V1.0.49: Kanban grouping mode — by status (mặc định) hoặc by category (loại task)
  const KANBAN_TYPE_KEY = 'hv-task-kanban-type';
  const [kanbanType, setKanbanType] = useState<'status' | 'category'>(() => {
    const saved = localStorage.getItem(KANBAN_TYPE_KEY);
    return (saved === 'category') ? 'category' : 'status';
  });
  useEffect(() => { localStorage.setItem(KANBAN_TYPE_KEY, kanbanType); }, [kanbanType]);

  // Kanban sort config: persist riêng cho từng tài khoản (qua user.id trong key)
  const TASK_KANBAN_SORT_KEY = `hv-task-kanban-sort-${currentUser?.id ?? 'anon'}`;
  type KanbanSortKey = 'urgent_desc' | 'important_desc' | 'eisenhower' | 'duedate_asc' | 'duedate_desc' | 'created_desc' | 'created_asc' | 'title_asc';
  const [kanbanSorts, setKanbanSorts] = useState<KanbanSortKey[]>(() => {
    const saved = localStorage.getItem(TASK_KANBAN_SORT_KEY);
    if (!saved) return ['eisenhower'];
    // Backward compat: nếu là 1 string cũ thì wrap thành array
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string') return [parsed as KanbanSortKey];
    } catch {
      // saved là plain string từ version cũ
      const valid: KanbanSortKey[] = ['urgent_desc', 'important_desc', 'eisenhower', 'duedate_asc', 'duedate_desc', 'created_desc', 'created_asc', 'title_asc'];
      if (valid.includes(saved as KanbanSortKey)) return [saved as KanbanSortKey];
    }
    return ['eisenhower'];
  });
  useEffect(() => {
    localStorage.setItem(TASK_KANBAN_SORT_KEY, JSON.stringify(kanbanSorts));
  }, [kanbanSorts, TASK_KANBAN_SORT_KEY]);

  useEffect(() => { saveVisibleColumns(visibleColumns); }, [visibleColumns]);

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', { tab, search }],
    // V1.0.63i: pageSize lớn để filter trạng thái/tag/hạn (client-side) hoạt động đúng
    // (trước đây default 20 → filter chỉ áp trên 20 task đầu, status cần tìm ở trang sau bị ẩn)
    queryFn: () => taskService.list({ tab, search, pageSize: 500 } as any),
  });

  const { data: counts } = useQuery({
    queryKey: ['task-my-counts'],
    queryFn: () => taskService.myCounts(),
    staleTime: 30 * 1000,
  });

  const filteredData = useMemo(() => {
    let items = data?.items ?? [];
    // V1.0.63i: OVERDUE không phải status thật trong DB (task quá hạn vẫn giữ status gốc).
    // → Filter "Quá hạn" tính theo dueDate < hôm nay + chưa hoàn thành/hủy.
    if (statusFilter === 'OVERDUE') {
      items = items.filter((t: Task) =>
        t.dueDate
        && dayjs(t.dueDate).isBefore(dayjs(), 'day')
        && t.status !== 'COMPLETED'
        && t.status !== 'CANCELLED'
        && t.status !== 'ARCHIVED'
      );
    } else if (statusFilter) {
      items = items.filter((t: Task) => t.status === statusFilter);
    }
    // V1.0.26: filter theo trục Gấp + Quan trọng (bỏ priorityFilter cũ)
    if (urgentLevelFilter !== undefined) items = items.filter((t: any) => (t.priorityLevel ?? 3) === urgentLevelFilter);
    if (importantLevelFilter !== undefined) items = items.filter((t: any) => (t.importantLevel ?? 3) === importantLevelFilter);

    if (tagFilter.length > 0) {
      items = items.filter((t: any) => {
        const tags: string[] = t.tags ?? [];
        // Match ít nhất 1 tag (OR)
        return tagFilter.some(tag => tags.includes(tag));
      });
    }

    if (dueFrom || dueTo) {
      items = items.filter((t: Task) => {
        if (!t.dueDate) return false;
        const due = dayjs(t.dueDate);
        if (dueFrom && due.isBefore(dueFrom, 'day')) return false;
        if (dueTo && due.isAfter(dueTo, 'day')) return false;
        return true;
      });
    }
    return items;
  }, [data, statusFilter, urgentLevelFilter, importantLevelFilter, tagFilter, dueFrom, dueTo]);

  // Aggregate tags from all items để làm select options
  const availableTags = useMemo(() => {
    const s = new Set<string>();
    (data?.items ?? []).forEach((t: any) => (t.tags ?? []).forEach((x: string) => s.add(x)));
    return Array.from(s).sort();
  }, [data]);

  const antColumns = useMemo(() => {
    return ALL_COLUMNS
      .filter(c => visibleColumns.has(c.key))
      .map(c => ({
        title: c.label,
        dataIndex: c.key,
        width: c.width,
        render: c.render,
      }));
  }, [visibleColumns]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const allData = await taskService.list({ tab, search, pageSize: 1000 } as any);
      const items: Task[] = allData?.items ?? [];

      const cols = ALL_COLUMNS.filter(c => visibleColumns.has(c.key));
      const rows = items.map(t => {
        const row: Record<string, any> = {};
        cols.forEach(c => {
          row[c.label] = c.excelValue ? c.excelValue(t) : '';
        });
        return row;
      });

      if (rows.length === 0) {
        message.warning('Không có dữ liệu để xuất');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      const colWidths = cols.map(c => ({
        wch: Math.max(c.label.length, ...rows.map(r => String(r[c.label] ?? '').length)) + 2,
      }));
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Danh sách task');

      const tabNameMap: Record<Tab, string> = {
        'assigned-to-me': 'toi-duoc-giao',
        'assigned-by-me': 'toi-giao',
        'watching': 'theo-doi',
        'collaborating': 'phoi-hop',
        'my-department': 'khoa-toi',
        'archived': 'luu-tru',
        'all': 'tat-ca',
      };
      const filename = `tasks_${tabNameMap[tab]}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;

      XLSX.writeFile(wb, filename);
      message.success(`Đã xuất ${rows.length} task ra ${filename}`);
    } catch (e: any) {
      message.error('Lỗi xuất Excel: ' + (e?.message ?? 'Unknown'));
    } finally {
      setExporting(false);
    }
  };

  const toggleColumn = (key: ColumnKey, checked: boolean) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      if (next.size === 0) next.add('title');
      return next;
    });
  };

  const resetColumns = () => {
    setVisibleColumns(new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key)));
  };

  return (
    <>
      <PageHeader
        breadcrumb="TRANG CHỦ / DANH SÁCH TASK"
        title="Danh sách task"
        extra={
          <>
            <Button icon={<Settings size={14} />} onClick={() => setColumnModalOpen(true)}>
              Cột hiển thị ({visibleColumns.size})
            </Button>
            <Button icon={<Download size={14} />} onClick={handleExport} loading={exporting}>
              Xuất Excel
            </Button>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => navigate('/tasks/new')}>
              Tạo task
            </Button>
          </>
        }
      />

      <div style={{ background: '#fff', border: '1px solid #d4d9df', borderRadius: 8, overflow: 'hidden' }}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as Tab)}
          style={{ padding: '0 20px', borderBottom: '1px solid #eceef1' }}
          items={[
            { key: 'assigned-to-me', label: `Tôi được giao (${counts?.assignedToMe ?? 0})` },
            { key: 'assigned-by-me', label: `Tôi giao (${counts?.assignedByMe ?? 0})` },
            // V1.0.63h: Bỏ tab "Tôi tạo", thêm 2 tab "Task theo dõi" + "Task phối hợp"
            { key: 'watching', label: `Task theo dõi (${counts?.watching ?? 0})` },
            { key: 'collaborating', label: `Task phối hợp (${counts?.collaborating ?? 0})` },
            // V1.0.49: Tab "Task khoa tôi" chỉ DEPT_HEAD/BOD/SYS_ADMIN mới thấy
            ...(isDeptHead ? [{
              key: 'my-department',
              label: `Khoa tôi quản (${counts?.myDepartment ?? 0})`,
            }] : []),
            { key: 'all', label: canViewAll ? 'Tất cả (toàn hệ thống)' : 'Tất cả (của tôi)' },
            // V1.0.51: Tab Lưu trữ — task ARCHIVED (COMPLETED >1 ngày + CANCELLED)
            { key: 'archived', label: `Lưu trữ (${counts?.archived ?? 0})` },
          ]}
        />

        <div style={{ padding: 16, borderBottom: '1px solid #eceef1', background: '#f6f7f8' }}>
          <Space wrap>
            <Input
              placeholder="Tìm theo tiêu đề, mã..."
              prefix={<Search size={14} style={{ color: '#7d8a98' }} />}
              allowClear
              style={{ width: 260 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select placeholder="Trạng thái" style={{ width: 140 }} allowClear
              value={statusFilter} onChange={setStatusFilter}
              options={Object.entries(STATUS_LABELS).map(([v, label]) => ({ value: v, label }))} />
            {/* V1.0.26: 2 trục Eisenhower thay cho Độ ưu tiên cũ — Gấp + Quan trọng cấp 1-5 */}
            <Select placeholder="🔴 Gấp" style={{ width: 130 }} allowClear
              value={urgentLevelFilter} onChange={setUrgentLevelFilter}
              options={[
                { value: 5, label: 'Gấp Cấp 5' },
                { value: 4, label: 'Gấp Cấp 4' },
                { value: 3, label: 'Gấp Cấp 3' },
                { value: 2, label: 'Gấp Cấp 2' },
                { value: 1, label: 'Gấp Cấp 1' },
              ]} />
            <Select placeholder="🟠 Quan trọng" style={{ width: 150 }} allowClear
              value={importantLevelFilter} onChange={setImportantLevelFilter}
              options={[
                { value: 5, label: 'Quan trọng Cấp 5' },
                { value: 4, label: 'Quan trọng Cấp 4' },
                { value: 3, label: 'Quan trọng Cấp 3' },
                { value: 2, label: 'Quan trọng Cấp 2' },
                { value: 1, label: 'Quan trọng Cấp 1' },
              ]} />
            <Select
              mode="multiple"
              placeholder="Tags"
              style={{ minWidth: 180 }}
              allowClear
              maxTagCount="responsive"
              value={tagFilter}
              onChange={setTagFilter}
              options={availableTags.map(t => ({ value: t, label: t }))}
            />
            <DatePicker
              placeholder="Hạn từ"
              format="DD/MM/YYYY"
              value={dueFrom}
              onChange={setDueFrom}
              style={{ width: 130 }}
            />
            <DatePicker
              placeholder="Hạn đến"
              format="DD/MM/YYYY"
              value={dueTo}
              onChange={setDueTo}
              style={{ width: 130 }}
            />
            {(tagFilter.length > 0 || dueFrom || dueTo || statusFilter || urgentLevelFilter !== undefined || importantLevelFilter !== undefined) && (
              <Button size="small" onClick={() => {
                setTagFilter([]); setDueFrom(null); setDueTo(null);
                setStatusFilter(undefined);
                setUrgentLevelFilter(undefined); setImportantLevelFilter(undefined);
              }}>Xóa lọc</Button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* Kanban sort - multi-select. Chỉ hiện khi đang xem kanban */}
              {viewMode === 'kanban' && (
                <Tooltip title="Có thể chọn nhiều tiêu chí — sắp theo thứ tự ưu tiên (chọn trước = ưu tiên cao hơn)">
                  <Select
                    size="small"
                    mode="multiple"
                    value={kanbanSorts}
                    onChange={(v: any) => setKanbanSorts(v.length === 0 ? ['eisenhower'] : v)}
                    style={{ minWidth: 280 }}
                    placeholder="Sắp xếp..."
                    maxTagCount={2}
                    options={[
                      { value: 'eisenhower', label: '⭐ Quan trọng + Gấp' },
                      { value: 'urgent_desc', label: '🔴 Gấp giảm dần' },
                      { value: 'important_desc', label: '🟠 Quan trọng giảm dần' },
                      { value: 'duedate_asc', label: '📅 Hạn gần nhất' },
                      { value: 'duedate_desc', label: '📅 Hạn xa nhất' },
                      { value: 'created_desc', label: '🕐 Mới tạo nhất' },
                      { value: 'created_asc', label: '🕐 Cũ nhất' },
                      { value: 'title_asc', label: '🔤 Tên A → Z' },
                    ]}
                  />
                </Tooltip>
              )}
              {/* V1.0.49: Toggle nhóm Kanban — chỉ hiện khi đang ở Kanban view */}
              {viewMode === 'kanban' && (
                <Tooltip title="Cách nhóm các cột Kanban">
                  <Segmented
                    size="small"
                    value={kanbanType}
                    onChange={(v) => setKanbanType(v as 'status' | 'category')}
                    options={[
                      { value: 'status', label: 'Theo trạng thái' },
                      { value: 'category', label: 'Theo loại task' },
                    ]}
                  />
                </Tooltip>
              )}
              <Segmented
                size="small"
                value={viewMode}
                onChange={(v) => setViewMode(v as 'list' | 'kanban')}
                options={[
                  { value: 'list', icon: <LayoutList size={14} />, label: '' },
                  { value: 'kanban', icon: <LayoutGrid size={14} />, label: '' },
                ]}
              />
            </div>
          </Space>
        </div>

        {viewMode === 'list' ? (
          <Table
            loading={isLoading}
            size="middle"
            columns={antColumns}
            dataSource={filteredData}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            rowClassName={(task: any) => {
              // V1.0.63h: Task quá hạn — tô nền đỏ nhẹ + border-left đỏ đậm (priority cao nhất)
              const isOverdue = task.status === 'OVERDUE'
                || (task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day')
                    && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && task.status !== 'ARCHIVED');
              if (isOverdue) return 'hv-task-row-overdue';
              // V1.0.26: highlight nếu Gấp ≥ 3 hoặc Quan trọng ≥ 3
              const urgent = task.priorityLevel ?? 3;
              const important = task.importantLevel ?? 3;
              if (urgent >= 4 || important >= 4) return 'hv-task-row-urgent'; // ít nhất 1 cấp 4-5 = đỏ đậm
              if (urgent >= 3 || important >= 3) return 'hv-task-row-important'; // cấp 3 = vàng nhạt
              return '';
            }}
            onRow={(task) => ({
              onClick: () => navigate(`/tasks/${task.id}`),
              style: { cursor: 'pointer' },
            })}
            pagination={{ pageSize: 20, showTotal: (t) => `Tổng ${t} task` }}
          />
        ) : (
          <TaskKanbanView
            tasks={filteredData}
            loading={isLoading}
            navigate={navigate}
            sortBy={kanbanSorts}
            kanbanType={kanbanType}
            currentUserId={currentUser?.id}
            tab={tab}
          />
        )}
      </div>

      <Modal
        title="Cấu hình cột hiển thị"
        open={columnModalOpen}
        onCancel={() => setColumnModalOpen(false)}
        width={500}
        footer={[
          <Button key="reset" onClick={resetColumns}>Mặc định</Button>,
          <Button key="close" type="primary" onClick={() => setColumnModalOpen(false)}>Xong</Button>,
        ]}
      >
        <p style={{ color: '#7d8a98', fontSize: 12, marginBottom: 12 }}>
          Tích chọn cột cần hiển thị. Cấu hình được lưu trên trình duyệt này.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {ALL_COLUMNS.map(c => (
            <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
                   border: '1px solid #eceef1', borderRadius: 4, cursor: 'pointer',
                   background: visibleColumns.has(c.key) ? '#f0f7ff' : '#fff' }}>
              <Checkbox
                checked={visibleColumns.has(c.key)}
                onChange={(e) => toggleColumn(c.key, e.target.checked)}
              />
              <span style={{ fontSize: 13 }}>{c.label}</span>
            </label>
          ))}
        </div>
      </Modal>
    </>
  );
}

// ============================================================
// Kanban view cho TaskListPage — group theo status
// ============================================================
const TASK_STATUS_GROUPS: { key: string; label: string; statuses: string[]; color: string; bg: string }[] = [
  { key: 'todo',        label: 'CHƯA LÀM',       statuses: ['NEW', 'ASSIGNED', 'TODO', 'OPEN'], color: '#5b6875', bg: '#f0f4f8' },
  { key: 'in_progress', label: 'ĐANG THỰC HIỆN', statuses: ['IN_PROGRESS'],                     color: '#1976D2', bg: '#e6f4ff' },
  { key: 'review',      label: 'CHỜ DUYỆT',      statuses: ['PENDING_REVIEW'],                   color: '#fa8c16', bg: '#fff7e6' },
  { key: 'completed',   label: 'HOÀN THÀNH',     statuses: ['COMPLETED', 'DONE'],                color: '#52c41a', bg: '#e6ffed' },
];

// V1.0.63i: Kanban groups riêng cho TAB LƯU TRỮ — task ARCHIVED chứa COMPLETED + CANCELLED
const TASK_ARCHIVE_STATUS_GROUPS: { key: string; label: string; statuses: string[]; color: string; bg: string }[] = [
  { key: 'completed',   label: 'HOÀN THÀNH',     statuses: ['COMPLETED', 'DONE'],          color: '#52c41a', bg: '#e6ffed' },
  { key: 'cancelled',   label: 'ĐÃ HỦY',         statuses: ['CANCELLED', 'CANCELED'],      color: '#cf1322', bg: '#fff1f0' },
  { key: 'archived',    label: 'ĐÃ LƯU TRỮ',     statuses: ['ARCHIVED'],                   color: '#722ed1', bg: '#f9f0ff' },
  { key: 'other',       label: 'TRẠNG THÁI KHÁC', statuses: ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'PENDING_REVIEW', 'TODO', 'OPEN'], color: '#5b6875', bg: '#f0f4f8' },
];

// V1.0.49: Kanban theo loại task — 4 cột phân theo bản chất task
const TASK_CATEGORY_GROUPS: { key: string; label: string; color: string; bg: string; hint: string }[] = [
  { key: 'normal',    label: 'TASK THÔNG THƯỜNG', color: '#5b6875', bg: '#f0f4f8', hint: 'Không thuộc dự án, không có task con' },
  { key: 'project',   label: 'TASK THEO DỰ ÁN',   color: '#1976D2', bg: '#e6f4ff', hint: 'Thuộc 1 dự án cụ thể' },
  { key: 'rollup',    label: 'TỔNG HỢP TASK',     color: '#722ed1', bg: '#f9f0ff', hint: 'Toàn bộ task (gộp 3 loại còn lại)' },
  { key: 'watching',  label: 'TASK TÔI THEO DÕI', color: '#fa8c16', bg: '#fff7e6', hint: 'Task tôi là người theo dõi (Watcher)' },
];

function TaskKanbanView({ tasks, loading, navigate, sortBy, kanbanType = 'status', currentUserId, tab }: {
  tasks: Task[];
  loading: boolean;
  navigate: (path: string) => void;
  sortBy?: Array<'urgent_desc' | 'important_desc' | 'eisenhower' | 'duedate_asc' | 'duedate_desc' | 'created_desc' | 'created_asc' | 'title_asc'>;
  kanbanType?: 'status' | 'category';
  currentUserId?: string;
  tab?: string;
}) {
  // V1.0.35: Bỏ drag-drop + status mutation — chỉ đổi trạng thái trong chi tiết task

  // V1.0.53: Pagination per column — mặc định mỗi cột show 5 task, "Xem thêm" cộng 5 mỗi lần
  const KANBAN_PAGE_SIZE = 5;
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  // Reset visibleCounts khi kanbanType đổi (vì group keys khác)
  useEffect(() => {
    setVisibleCounts({});
  }, [kanbanType]);

  const getVisibleCount = (groupKey: string): number => {
    return visibleCounts[groupKey] ?? KANBAN_PAGE_SIZE;
  };
  const showMore = (groupKey: string) => {
    setVisibleCounts(prev => ({
      ...prev,
      [groupKey]: (prev[groupKey] ?? KANBAN_PAGE_SIZE) + KANBAN_PAGE_SIZE,
    }));
  };

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center' }}>Đang tải...</div>;
  }

  // Comparator cho 1 tiêu chí
  const cmpOne = (a: any, b: any, key: string): number => {
    const aUrg = a.priorityLevel ?? 3;
    const bUrg = b.priorityLevel ?? 3;
    const aImp = a.importantLevel ?? 3;
    const bImp = b.importantLevel ?? 3;
    switch (key) {
      case 'urgent_desc':
        return bUrg - aUrg;
      case 'important_desc':
        return bImp - aImp;
      case 'eisenhower': {
        const aScore = aImp * 5 + aUrg;
        const bScore = bImp * 5 + bUrg;
        return bScore - aScore;
      }
      case 'duedate_asc': {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf();
      }
      case 'duedate_desc': {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return dayjs(b.dueDate).valueOf() - dayjs(a.dueDate).valueOf();
      }
      case 'created_desc':
        return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
      case 'created_asc':
        return dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
      case 'title_asc':
        return (a.title ?? '').localeCompare(b.title ?? '', 'vi');
      default:
        return 0;
    }
  };

  // Multi-criteria comparator: chain compare từng tiêu chí, tie-break bằng tiêu chí kế tiếp
  const cmp = (a: any, b: any): number => {
    const sorts = (sortBy && sortBy.length > 0) ? sortBy : ['eisenhower'];
    for (const key of sorts) {
      const result = cmpOne(a, b, key);
      if (result !== 0) return result;
    }
    return 0;
  };

  // V1.0.49: Chọn groups dựa trên kanbanType (status hoặc category)
  // V1.0.63i: Tab "archived" dùng bộ groups riêng (Hoàn thành / Đã hủy / Đã lưu trữ)
  type GroupDef = { key: string; label: string; color: string; bg: string; hint?: string };
  const isArchiveTab = tab === 'archived';
  const statusGroups = isArchiveTab ? TASK_ARCHIVE_STATUS_GROUPS : TASK_STATUS_GROUPS;
  const activeGroups: GroupDef[] = kanbanType === 'category'
    ? TASK_CATEGORY_GROUPS
    : statusGroups;

  // Group tasks - lưu ý: ở mode 'category', 1 task có thể xuất hiện ở NHIỀU cột
  // (vd task con của dự án mà tôi là watcher → xuất hiện cả "Theo dự án" + "Tôi theo dõi" + "Tổng hợp")
  const grouped: Record<string, Task[]> = {};
  for (const g of activeGroups) grouped[g.key] = [];

  if (kanbanType === 'status') {
    for (const t of tasks) {
      const grp = statusGroups.find(g => g.statuses.includes(t.status));
      if (grp) {
        grouped[grp.key].push(t);
      } else if (isArchiveTab) {
        // V1.0.63i: Task lưu trữ có status lạ → đẩy vào cột "Trạng thái khác"
        grouped['other']?.push(t);
      }
    }
  } else {
    // kanbanType === 'category'
    for (const t of tasks) {
      const ta = t as any;
      const hasProject = !!ta.projectId;
      const isWatcher = currentUserId && (ta.watchers ?? []).some((w: any) => w.id === currentUserId);

      // 'rollup' = tổng hợp toàn bộ → mọi task đều vào đây
      grouped.rollup.push(t);

      if (isWatcher) {
        grouped.watching.push(t);
      }
      if (hasProject) {
        grouped.project.push(t);
      } else {
        // Không thuộc dự án + không phải watcher-only → task thông thường
        if (!isWatcher) {
          grouped.normal.push(t);
        } else {
          // Task tôi theo dõi mà không thuộc dự án → vẫn vào "thông thường" để user thấy đầy đủ
          grouped.normal.push(t);
        }
      }
    }
  }

  // Apply sort trong từng group
  for (const k of Object.keys(grouped)) {
    grouped[k] = [...grouped[k]].sort(cmp);
  }

  // V1.0.35: Bỏ drag-drop — đổi status chỉ trong chi tiết task

  return (
    <div style={{ padding: 16 }}>
      <div className="hv-kanban-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))',
        gap: 12,
        overflowX: 'auto',
      }}>
        {activeGroups.map(grp => {
          const items = grouped[grp.key];
          // V1.0.53: Pagination — chỉ render 5 đầu, có nút "Xem thêm" để load thêm
          const visibleCount = getVisibleCount(grp.key);
          const visibleItems = items.slice(0, visibleCount);
          const hasMore = items.length > visibleCount;
          const remaining = items.length - visibleCount;
          return (
            <div
              key={grp.key}
              style={{
                background: '#fff',
                border: '1px solid #e8edf2',
                borderTop: `3px solid ${grp.color}`,
                borderRadius: 8,
                padding: 8,
                minHeight: 200,
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
              <div style={{
                padding: '4px 8px',
                fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                color: grp.color, textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                {grp.hint ? (
                  <Tooltip title={grp.hint}>
                    <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>
                      {grp.label}
                    </span>
                  </Tooltip>
                ) : (
                  <span>{grp.label}</span>
                )}
                <span style={{
                  background: grp.bg, color: grp.color,
                  borderRadius: 10, padding: '0 8px',
                  fontSize: 11, minWidth: 22, textAlign: 'center',
                }}>{items.length}</span>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.length === 0 ? (
                  <div style={{
                    padding: 30, textAlign: 'center', fontSize: 11,
                    color: '#bdc4cd',
                  }}>
                    Trống
                  </div>
                ) : visibleItems.map(t => {
                  // V1.0.26: highlight theo Gấp + Quan trọng cấp 3-5
                  const urgentLvl = (t as any).priorityLevel ?? 3;
                  const importantLvl = (t as any).importantLevel ?? 3;

                  // Tính số ngày còn lại / quá hạn (chỉ task chưa COMPLETED)
                  let daysInfo: { text: string; color: string; bg: string } | null = null;
                  if (t.dueDate && t.status !== 'COMPLETED' && t.status !== 'CANCELLED') {
                    const due = dayjs(t.dueDate).startOf('day');
                    const today = dayjs().startOf('day');
                    const diffDays = due.diff(today, 'day');
                    if (diffDays < 0) {
                      // Quá hạn
                      const overdue = Math.abs(diffDays);
                      daysInfo = {
                        text: `⚠ Quá hạn ${overdue} ngày`,
                        color: '#cf1322',
                        bg: '#fff1f0',
                      };
                    } else if (diffDays === 0) {
                      daysInfo = { text: '🔥 Hết hạn hôm nay', color: '#d46b08', bg: '#fff7e6' };
                    } else if (diffDays === 1) {
                      daysInfo = { text: '⏰ Còn 1 ngày', color: '#d46b08', bg: '#fff7e6' };
                    } else if (diffDays <= 3) {
                      daysInfo = { text: `⏰ Còn ${diffDays} ngày`, color: '#d46b08', bg: '#fff7e6' };
                    } else if (diffDays <= 7) {
                      daysInfo = { text: `Còn ${diffDays} ngày`, color: '#1976D2', bg: '#e6f4ff' };
                    } else {
                      daysInfo = { text: `Còn ${diffDays} ngày`, color: '#5b6875', bg: '#f0f4f8' };
                    }
                  }

                  return (
                    <div
                      key={t.id}
                      onClick={() => navigate(`/tasks/${t.id}`)}
                      style={{
                        background: urgentLvl >= 4 ? '#fff5f5' : importantLvl >= 4 ? '#fffbe6'
                                  : (urgentLvl >= 3 || importantLvl >= 3) ? '#fffdf5' : '#fff',
                        border: `1px solid ${urgentLvl >= 4 ? '#ffa39e' : importantLvl >= 4 ? '#ffd591'
                                  : (urgentLvl >= 3 || importantLvl >= 3) ? '#ffe7ba' : '#e8edf2'}`,
                        borderLeft: urgentLvl >= 4 ? '4px solid #cf1322'
                                  : importantLvl >= 4 ? '4px solid #d46b08'
                                  : urgentLvl >= 3 ? '4px solid #fa8c16'
                                  : importantLvl >= 3 ? '4px solid #faad14'
                                  : '1px solid #e8edf2',
                        borderRadius: 6,
                        padding: '8px 10px',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        boxShadow: urgentLvl >= 4 ? '0 1px 3px rgba(207, 19, 34, 0.1)' : 'none',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = urgentLvl >= 4 ? '0 1px 3px rgba(207, 19, 34, 0.1)' : 'none';
                      }}
                    >
                      {/* V1.0.26: Banner LUÔN hiện cả 2 trục theo format "Gấp: Cấp X · Quan trọng: Cấp Y" */}
                      <div style={{
                        fontSize: 10, fontWeight: 700, marginBottom: 4,
                        letterSpacing: 0.3, display: 'flex', gap: 8, flexWrap: 'wrap',
                      }}>
                        <span style={{
                          color: urgentLvl >= 4 ? '#cf1322' : urgentLvl >= 3 ? '#fa8c16' : '#7d8a98',
                        }}>
                          🔴 Gấp: Cấp {urgentLvl}
                        </span>
                        <span style={{ color: '#bdc4cd' }}>·</span>
                        <span style={{
                          color: importantLvl >= 4 ? '#d46b08' : importantLvl >= 3 ? '#faad14' : '#7d8a98',
                        }}>
                          🟠 Quan trọng: Cấp {importantLvl}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, lineHeight: 1.3, color: '#1a1e23' }}>
                        {t.title}
                      </div>

                      {/* Row: số ngày còn / quá hạn — full width nổi bật */}
                      {daysInfo && (
                        <div style={{
                          fontSize: 11, fontWeight: 600,
                          color: daysInfo.color,
                          background: daysInfo.bg,
                          borderRadius: 4,
                          padding: '3px 8px',
                          marginBottom: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}>
                          <CalIcon size={11} />
                          {daysInfo.text}
                          {t.dueDate && (
                            <span style={{ fontWeight: 400, marginLeft: 4 }}>
                              · {dayjs(t.dueDate).format('DD/MM')}
                            </span>
                          )}
                        </div>
                      )}

                      {/* V1.0.53: Compact people display - Avatar + tên MAIN + "+N" */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 }}>
                        {/* Người thực hiện - MAIN + tên + +N */}
                        {t.assignees && t.assignees.length > 0 && (() => {
                          const mainAssignees = t.assignees.filter((a: any) => a.role === 'MAIN');
                          const supportCount = t.assignees.length - mainAssignees.length;
                          const display = mainAssignees.length > 0 ? mainAssignees : t.assignees.slice(0, 1);
                          const firstName = display[0]?.user?.fullName ?? (display[0] as any)?.fullName ?? '?';
                          const extraCount = (mainAssignees.length > 1 ? mainAssignees.length - 1 : 0) + supportCount;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                              <span style={{ color: '#7d8a98', fontSize: 10, minWidth: 56 }}>👤 Thực hiện:</span>
                              <Tooltip
                                title={
                                  <div style={{ fontSize: 11 }}>
                                    {t.assignees.map((a: any, idx: number) => (
                                      <div key={idx}>
                                        {a.user?.fullName ?? a.fullName} {a.role === 'MAIN' ? '(Chính)' : '(Hỗ trợ)'}
                                      </div>
                                    ))}
                                  </div>
                                }
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'help', flex: 1, minWidth: 0 }}>
                                  <Avatar size={20} style={{ background: '#1976D2', color: '#fff', fontSize: 10, flexShrink: 0 }}>
                                    {firstName.slice(-1)}
                                  </Avatar>
                                  <span style={{
                                    color: '#1976D2', fontWeight: 600,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    flex: 1, minWidth: 0,
                                  }}>
                                    {firstName}
                                  </span>
                                  {extraCount > 0 && (
                                    <span style={{
                                      background: '#e6f4ff', color: '#1976D2', fontWeight: 700,
                                      fontSize: 10, padding: '0 5px', borderRadius: 8, flexShrink: 0,
                                    }}>
                                      +{extraCount}
                                    </span>
                                  )}
                                </div>
                              </Tooltip>
                            </div>
                          );
                        })()}

                        {/* Người giao - tên đầy đủ (chỉ 1 người) */}
                        {t.assigner?.fullName && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                            <span style={{ color: '#7d8a98', fontSize: 10, minWidth: 56 }}>📤 Giao bởi:</span>
                            <Avatar size={20} style={{ background: '#d4d9df', color: '#5b6875', fontSize: 10, flexShrink: 0 }}>
                              {t.assigner.fullName.slice(-1)}
                            </Avatar>
                            <span style={{
                              color: '#5b6875', fontWeight: 500,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0,
                            }}>
                              {t.assigner.fullName}
                            </span>
                          </div>
                        )}

                        {/* V1.0.53: Người theo dõi - avatar đầu + +N */}
                        {t.watchers && t.watchers.length > 0 && (() => {
                          const first = t.watchers[0];
                          const extraCount = t.watchers.length - 1;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                              <span style={{ color: '#7d8a98', fontSize: 10, minWidth: 56 }}>👁 Theo dõi:</span>
                              <Tooltip
                                title={
                                  <div style={{ fontSize: 11 }}>
                                    {t.watchers.map((w: any, idx: number) => (
                                      <div key={idx}>{w.fullName}</div>
                                    ))}
                                  </div>
                                }
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'help', flex: 1, minWidth: 0 }}>
                                  <Avatar size={20} style={{ background: '#fa8c16', color: '#fff', fontSize: 10, flexShrink: 0 }}>
                                    {first.fullName?.slice(-1)}
                                  </Avatar>
                                  <span style={{
                                    color: '#d46b08', fontWeight: 500,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0,
                                  }}>
                                    {first.fullName}
                                  </span>
                                  {extraCount > 0 && (
                                    <span style={{
                                      background: '#fff7e6', color: '#d46b08', fontWeight: 700,
                                      fontSize: 10, padding: '0 5px', borderRadius: 8, flexShrink: 0,
                                    }}>
                                      +{extraCount}
                                    </span>
                                  )}
                                </div>
                              </Tooltip>
                            </div>
                          );
                        })()}
                      </div>

                      <div style={{ fontSize: 10, color: '#7d8a98', fontWeight: 600 }}>
                        {t.code}
                      </div>
                    </div>
                  );
                })}

                {/* V1.0.53: Nút "Xem thêm" — load thêm 5 task mỗi lần click */}
                {hasMore && (
                  <button
                    onClick={() => showMore(grp.key)}
                    style={{
                      marginTop: 4,
                      padding: '8px 12px',
                      background: 'transparent',
                      border: `1px dashed ${grp.color}`,
                      borderRadius: 6,
                      color: grp.color,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      width: '100%',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = grp.bg;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    ⬇ Xem thêm ({Math.min(KANBAN_PAGE_SIZE, remaining)} / còn {remaining})
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
